import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Heart } from 'lucide-react';

interface AnimalRestoredToastProps {
  visible: boolean;
  animal: { name: string; slug: string };
  onDismiss: () => void;
}

export default function AnimalRestoredToast({ visible, animal, onDismiss }: AnimalRestoredToastProps) {
  const { t } = useTranslation('globe');

  useEffect(() => {
    if (visible) {
      window.gtag?.('event', 'globe_restoration', {
        animal_slug: animal.slug,
      });
      const timer = setTimeout(onDismiss, 2000);
      return () => clearTimeout(timer);
    }
  }, [visible, onDismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed top-4 inset-x-4 z-50"
          role="alert"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        >
          <button
            onClick={onDismiss}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-forest-wash border border-forest/20 shadow-lg"
          >
            <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-forest" />
            </div>
            <div className="flex-1 text-start min-w-0">
              <p className="text-small font-bold text-forest truncate">
                {t('restored.thriving', {
                  animalName: animal.name,
                  defaultValue: 'The {{animalName}} is thriving again.',
                })}
              </p>
            </div>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
